import React from 'react';
import { motion } from 'framer-motion';
import { BentoGrid, BentoCard } from './BentoGrid';
import { FadeUpOnScroll, StaggerOnScroll, StaggerItem } from './ScrollReveal';
import { GradientText } from './AnimatedText';
import { GridBackground } from './FloatingElements';
import { 
  ShieldCheck, ChefHat, Printer, Truck, LayoutGrid, WifiOff,
  CreditCard, BarChart3, Plug, Users, Smartphone, Clock,
  Brain, QrCode, Receipt, TrendingUp
} from 'lucide-react';

const mainFeatures = [
  {
    icon: ShieldCheck,
    title: 'TSE & KassenSichV',
    description: '100% finanzamtssicher. Jede Transaktion automatisch signiert und revisionssicher gespeichert.',
    colSpan: 'md:col-span-6 lg:col-span-5',
    rowSpan: 'row-span-2',
    variant: 'highlight',
    badge: 'Pflicht 2024'
  },
  {
    icon: ChefHat,
    title: 'Multi-Station Küche',
    description: 'Separate Displays für Küche, Grill, Fritteuse & Bar.',
    colSpan: 'md:col-span-3 lg:col-span-4',
    variant: 'default'
  },
  {
    icon: Brain,
    title: 'KI-Assistent',
    description: 'Speisekarte per Foto importieren. KI erkennt Gerichte automatisch.',
    colSpan: 'md:col-span-3 lg:col-span-3',
    variant: 'default',
    badge: 'NEU'
  },
  {
    icon: Printer,
    title: 'Smart Printing',
    description: 'Automatische Bon-Verteilung: Küche, Kasse, Tagesabschluss.',
    colSpan: 'md:col-span-3 lg:col-span-4',
    variant: 'default'
  },
  {
    icon: Truck,
    title: 'Lieferservice',
    description: 'Komplettes Liefermanagement mit Fahrerzuordnung.',
    colSpan: 'md:col-span-3 lg:col-span-3',
    variant: 'default'
  }
];

const secondaryFeatures = [
  { icon: LayoutGrid, title: 'Tischverwaltung', desc: 'Drag & Drop' },
  { icon: WifiOff, title: 'Offline-Modus', desc: 'Immer einsatzbereit' },
  { icon: CreditCard, title: 'Alle Zahlungsarten', desc: 'Bar, Karte, NFC' },
  { icon: BarChart3, title: 'DATEV-Export', desc: 'Direkte Schnittstelle' },
  { icon: Plug, title: 'Portal-Integration', desc: 'Lieferando, Wolt, Uber' },
  { icon: Users, title: 'Mitarbeiter', desc: 'Schichten & Rechte' },
  { icon: Smartphone, title: 'Kellner-App', desc: 'Bestellung am Tisch' },
  { icon: Clock, title: '24/7 Support', desc: 'Deutscher Support' },
  { icon: QrCode, title: 'QR-Bestellung', desc: 'Gäste bestellen selbst' },
  { icon: Receipt, title: 'Bewirtungsbeleg', desc: 'Per Knopfdruck' },
  { icon: TrendingUp, title: 'Analytics', desc: 'Echtzeit-Statistiken' }
];

export const FeaturesBentoUltimate = () => {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: '#0B1220' }}
    >
      <GridBackground />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <FadeUpOnScroll className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
            <span className="text-blue-400 text-sm font-medium">Über 50+ Funktionen</span>
          </div>
          <h2 
            data-testid="features-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Alles für Ihre <GradientText>Gastronomie</GradientText>
          </h2>
          <p className="mt-6 text-lg sm:text-xl max-w-3xl mx-auto text-slate-400">
            Von der Bestellaufnahme bis zum Steuerexport – Cash-X deckt den gesamten Workflow ab.
          </p>
        </FadeUpOnScroll>

        {/* Main Bento Grid */}
        <BentoGrid className="mb-12">
          {mainFeatures.map((feature, i) => (
            <BentoCard
              key={feature.title}
              colSpan={feature.colSpan}
              rowSpan={feature.rowSpan}
              variant={feature.variant}
              delay={i * 0.1}
              glowColor={feature.variant === 'highlight' ? 'rgba(16,185,129,0.15)' : 'rgba(47,128,255,0.1)'}
            >
              {/* Badge */}
              {feature.badge && (
                <div className="absolute top-4 right-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    feature.badge === 'NEU' 
                      ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                      : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  }`}>
                    {feature.badge}
                  </span>
                </div>
              )}
              
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                feature.variant === 'highlight' ? 'bg-emerald-500/20' : 'bg-slate-700/50'
              }`}>
                <feature.icon className={`h-7 w-7 ${
                  feature.variant === 'highlight' ? 'text-emerald-400' : 'text-blue-400'
                }`} />
              </div>
              
              {/* Content */}
              <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {feature.title}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {feature.description}
              </p>
              
              {/* Extra content for large card */}
              {feature.variant === 'highlight' && (
                <div className="mt-6 pt-6 border-t border-slate-700/50">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                      <span className="text-sm text-slate-300">Cloud-TSE</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-blue-400 rounded-full" />
                      <span className="text-sm text-slate-300">GoBD</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-amber-400 rounded-full" />
                      <span className="text-sm text-slate-300">DSFinV-K</span>
                    </div>
                  </div>
                </div>
              )}
            </BentoCard>
          ))}
        </BentoGrid>

        {/* Secondary Features Grid */}
        <StaggerOnScroll 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
          staggerDelay={0.05}
        >
          {secondaryFeatures.map((feature) => (
            <StaggerItem key={feature.title}>
              <motion.div
                whileHover={{ y: -4, scale: 1.02 }}
                className="group p-4 bg-[#111C32]/60 backdrop-blur-sm border border-slate-700/40 rounded-xl hover:border-blue-500/40 transition-all cursor-default"
              >
                <feature.icon className="h-5 w-5 text-slate-400 group-hover:text-blue-400 transition-colors mb-2" />
                <p className="text-sm font-medium text-white">{feature.title}</p>
                <p className="text-xs text-slate-500">{feature.desc}</p>
              </motion.div>
            </StaggerItem>
          ))}
        </StaggerOnScroll>

        {/* Screenshot Showcase Section */}
        <FadeUpOnScroll className="mt-24 md:mt-32">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Intuitive Benutzeroberfläche
            </h3>
            <p className="mt-4 text-lg text-slate-400">
              Moderne, übersichtliche Oberfläche – von Gastronomen für Gastronomen.
            </p>
          </div>
          
          {/* Main Screenshot */}
          <div className="relative mb-8">
            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
              className="rounded-2xl overflow-hidden shadow-2xl mx-auto max-w-5xl"
              style={{
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 25px 80px -12px rgba(0,0,0,0.6)'
              }}
            >
              <img 
                src="/screenshots/dashboard.jpg" 
                alt="Cash-X Kassensystem - Point of Sale"
                className="w-full"
                loading="lazy"
              />
            </motion.div>
            
            {/* Decorative Glow */}
            <div 
              className="absolute -inset-10 -z-10 opacity-30 blur-3xl"
              style={{
                background: 'radial-gradient(circle at center, rgba(47,128,255,0.3) 0%, transparent 70%)'
              }}
            />
          </div>

          {/* Screenshot Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {[
              { src: '/screenshots/produkte.jpg', label: 'Produktverwaltung' },
              { src: '/screenshots/tische.jpg', label: 'Tischverwaltung' },
              { src: '/screenshots/berichte.jpg', label: 'Berichte & Export' },
              { src: '/screenshots/team.jpg', label: 'Team-Verwaltung' }
            ].map((item, i) => (
              <motion.div
                key={item.src}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="relative group cursor-pointer"
              >
                <div 
                  className="rounded-xl overflow-hidden border border-slate-700/50 group-hover:border-blue-500/50 transition-all"
                  style={{ boxShadow: '0 10px 40px -10px rgba(0,0,0,0.5)' }}
                >
                  <img 
                    src={item.src} 
                    alt={item.label}
                    className="w-full aspect-video object-cover object-top"
                    loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <span className="text-white font-medium text-sm">{item.label}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </FadeUpOnScroll>
      </div>
    </section>
  );
};

export default FeaturesBentoUltimate;
